import { isAppPath } from './authDestination';

/**
 * "Use another account" signs the person out, and they have to come back to
 * the same page with the new account. Signing out loses it: with a password
 * the app reloads on `/login`, and with Google Cognito sends them back to the
 * origin. The page stores its path before signing out and the bootstrap puts
 * it back, so the auth machine takes it as the destination like on any other
 * load.
 *
 * Kept per tab (sessionStorage) and only for as long as an MCP connection in
 * progress lives: after that there is nothing to go back to.
 *
 * Bootstrap plumbing, like `hostedUIFailure.ts`: has to run before the auth
 * machine reads the location.
 */

const STORAGE_KEY = 'bilderit:destination-across-sign-out';
// As long as the MCP's pending login (oauth-server.ts).
const MAX_AGE_MS = 60 * 60 * 1000;

type Router = { replace: (path: string) => void };

export const rememberDestinationAcrossSignOut = (path: string) => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path, savedAt: Date.now() })
    );
  } catch {
    // Without storage the person lands on the home page, as before.
  }
};

export const restoreDestinationAfterSignOut = (history: Router) => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  try {
    const { path, savedAt }: { path: unknown; savedAt: unknown } =
      JSON.parse(raw);
    if (
      isAppPath(path) &&
      typeof savedAt === 'number' &&
      Date.now() - savedAt < MAX_AGE_MS
    ) {
      history.replace(path);
    }
  } catch {
    // Not a value of ours: dropped.
  }
};
